import { useEffect, useState, useCallback } from "react";
import superAdminApi from "../../../lib/superAdminApi";
import SuperAdminLayout from "../../../components/SuperAdminLayout";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "react-toastify";

interface StoreItem {
  id: string;
  name?: string | null;
  location?: string | null;
  contact_info?: string | null;
  created_at?: string | null;
  Business?: { id: string; name?: string | null } | null;
  _count: { Product: number; Sale: number; StoreAssignment: number };
}

const SuperAdminStores = () => {
  const [stores, setStores] = useState<StoreItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchStores = useCallback(() => {
    setLoading(true);
    superAdminApi
      .get("/superadmin/stores", { params: { page, limit: 20, search } })
      .then((res) => {
        setStores(res.data.stores);
        setTotal(res.data.total);
        setTotalPages(res.data.totalPages);
      })
      .catch((err) => toast.error(err.response?.data?.message ?? "Failed to load stores"))
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { fetchStores(); }, [fetchStores]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  return (
    <SuperAdminLayout>
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Stores</h1>
          <p className="text-gray-500 text-sm mt-1">{total.toLocaleString()} total stores</p>
        </div>

        <form onSubmit={handleSearch} className="flex gap-2 mb-5">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search stores..."
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <button type="submit" className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-500 transition-colors">
            Search
          </button>
        </form>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr className="text-left text-gray-500">
                  <th className="px-4 py-3 font-medium">Store</th>
                  <th className="px-4 py-3 font-medium">Business</th>
                  <th className="px-4 py-3 font-medium">Products</th>
                  <th className="px-4 py-3 font-medium">Sales</th>
                  <th className="px-4 py-3 font-medium">Staff</th>
                  <th className="px-4 py-3 font-medium">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-200 rounded w-32 mb-1.5" />
                        <div className="h-3 bg-gray-200 rounded w-24" />
                      </td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-28" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-8" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-8" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-8" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-20" /></td>
                    </tr>
                  ))
                ) : stores.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No stores found</td></tr>
                ) : (
                  stores.map((store) => (
                    <tr key={store.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{store.name ?? "Unnamed"}</p>
                        {store.location && <p className="text-xs text-gray-400">{store.location}</p>}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{store.Business?.name ?? "—"}</td>
                      <td className="px-4 py-3 text-gray-600">{store._count.Product}</td>
                      <td className="px-4 py-3 text-gray-600">{store._count.Sale}</td>
                      <td className="px-4 py-3 text-gray-600">{store._count.StoreAssignment}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs">
                        {store.created_at ? new Date(store.created_at).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 transition-colors"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}
        </div>
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminStores;
